import { useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Fish, Gift, RefreshCw, Wrench } from 'lucide-react';
import type { AdminApi, EconomyActivityEvent, EconomyActivityPlayer, EconomyActivitySummary, Environment, Platform } from '../api';

interface Props {
  api: AdminApi;
  gameId: string;
  environment: Environment;
  platform: Platform;
  notify: (message: string) => void;
}

const PAGE_SIZE = 50;
const TOOL_NAMES: Record<string, string> = {
  time_freeze: '冻鲜时钟', cat_rescue: '猫爪救援',
  carrier_clear: '整单速收', carrier_cell_cut: '单格维修',
};
const SOURCES: Record<string, string> = {
  initial_profile: '新玩家初始', round_settlement: '关卡通关',
  round_settlement_double: '通关广告翻倍', tool_purchase: '鱼干使用道具',
  timeout_continue: '超时续玩', free_tutorial: '首次教学',
  fish_dry: '鱼干购买', rewarded_ad: '激励广告', inventory: '奖励库存',
  daily_sign_in_base_fish_dry: '每日签到', daily_sign_in_bonus_fish_dry: '签到加班奖励',
  daily_benefit_first_fish_dry: '每日福利', daily_benefit_double_fish_dry: '每日福利翻倍',
  daily_benefit_second_fish_dry: '每日福利洗牌', daily_tasks_fish_dry: '每日任务',
  achievement_fish_dry: '成就奖励', achievement_milestone_fish_dry: '成就里程碑',
  game_club_daily_reward: '游戏圈奖励', desktop_entry_reward: '桌面有礼',
  test_gm_balance_set: '测试 GM 调账',
};

export function EconomyActivityCenter(props: Props) {
  const [dateKey, setDateKey] = useState(shanghaiToday);
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [snapshot, setSnapshot] = useState<EconomyActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<EconomyActivityPlayer | null>(null);
  const [events, setEvents] = useState<EconomyActivityEvent[]>([]);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    props.api.economyActivity(props.gameId, dateKey, props.environment, props.platform, PAGE_SIZE, offset)
      .then((value) => { if (!cancelled) setSnapshot(value); })
      .catch((error) => { if (!cancelled) props.notify(error instanceof Error ? error.message : '读取鱼干与道具流水失败'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.api, props.gameId, props.environment, props.platform, dateKey, offset, refresh]);

  useEffect(() => {
    setOffset(0);
    setSelected(null);
    setEvents([]);
  }, [props.environment, props.platform, dateKey]);

  const showPlayer = async (player: EconomyActivityPlayer) => {
    setSelected(player); setEvents([]); setHasMoreEvents(false); setEventsLoading(true);
    try {
      const result = await props.api.economyActivityEvents(props.gameId, player.playerId, dateKey, props.environment);
      setEvents(result.events); setHasMoreEvents(result.hasMore);
    } catch (error) {
      props.notify(error instanceof Error ? error.message : '读取玩家流水失败');
    } finally { setEventsLoading(false); }
  };

  const loadMore = async () => {
    if (!selected || eventsLoading) return;
    setEventsLoading(true);
    try {
      const result = await props.api.economyActivityEvents(props.gameId, selected.playerId, dateKey, props.environment, events.length);
      setEvents((current) => [...current, ...result.events]); setHasMoreEvents(result.hasMore);
    } catch (error) {
      props.notify(error instanceof Error ? error.message : '读取更多流水失败');
    } finally { setEventsLoading(false); }
  };

  const totals = snapshot;
  return <div className="page economy-activity-page">
    <section className="page-intro">
      <div><p className="eyebrow">AUTHORITATIVE LEDGER · ASIA/SHANGHAI</p>
        <h2>鱼干与道具流水</h2>
        <span>按上海自然日汇总全部玩家；鱼干以服务端台账为准，道具获得与实际使用分别统计。</span></div>
      <div className="intro-actions">
        <label className="economy-activity-date">日期<input type="date" value={dateKey} onChange={(event) => setDateKey(event.target.value)} /></label>
        <button className="secondary-button" onClick={() => setRefresh((value) => value + 1)}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />刷新
        </button>
      </div>
    </section>

    <section className="economy-activity-kpis">
      <article><Fish size={20} /><span>鱼干获得</span><strong>+{number(totals?.fishEarned)}</strong></article>
      <article><ArrowDownRight size={20} /><span>鱼干消耗</span><strong>−{number(totals?.fishSpent)}</strong></article>
      <article><Gift size={20} /><span>道具获得</span><strong>{number(totals?.toolsGranted)}</strong></article>
      <article><Wrench size={20} /><span>道具实际使用</span><strong>{number(totals?.toolsUsed)}</strong></article>
    </section>

    <section className="panel economy-activity-panel">
      <header><div><h3>逐玩家情况</h3><p>{dateKey} · 有流水 {number(totals?.playersWithActivity)} 人 / 共 {number(totals?.totalPlayers)} 人 · {platformName(props.platform)}</p></div></header>
      <div className="economy-activity-table">
        <div className="economy-activity-row heading"><span>玩家</span><span>获得鱼干</span><span>使用鱼干</span><span>获得道具</span><span>使用道具</span><span>明细</span></div>
        {totals?.players.map((player) => <div className="economy-activity-row" key={player.playerId}>
          <span className="economy-activity-player"><strong>{player.displayName}</strong><small>{platformName(player.platform)} · {player.playerId}</small></span>
          <span className="gain">+{number(player.fishEarned)}</span>
          <span className="spend">−{number(player.fishSpent)}</span>
          <span>{number(player.toolsGranted)}</span><span>{number(player.toolsUsed)}</span>
          <span><button className="text-button" onClick={() => void showPlayer(player)}>查看流水</button></span>
        </div>)}
        {!loading && !totals?.players.length && <p className="empty-state">当前筛选没有玩家</p>}
      </div>
      <footer className="economy-activity-pagination">
        <span>显示 {totals?.totalPlayers ? offset + 1 : 0}–{Math.min(offset + PAGE_SIZE, totals?.totalPlayers ?? 0)} / {number(totals?.totalPlayers)}</span>
        <div><button className="secondary-button" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>上一页</button>
          <button className="secondary-button" disabled={loading || offset + PAGE_SIZE >= (totals?.totalPlayers ?? 0)} onClick={() => setOffset(offset + PAGE_SIZE)}>下一页</button></div>
      </footer>
    </section>

    {selected && <section className="panel economy-activity-detail">
      <header><div><h3>{selected.displayName} · {dateKey}</h3><p>获得道具包括奖励库存、首次教学和一次性广告／鱼干授权；使用只计实际消费。</p></div>
        <button className="secondary-button" onClick={() => setSelected(null)}>收起</button></header>
      <div className="economy-activity-events">
        {events.map((event) => <div className="economy-activity-event" key={event.id}>
          <span className={`economy-event-icon ${event.kind}`}>
            {event.kind === 'fish' ? event.fishDryDelta! >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} /> : event.kind === 'tool_granted' ? <Gift size={16} /> : <Wrench size={16} />}
          </span>
          <div><strong>{event.kind === 'fish' ? event.fishDryDelta! >= 0 ? '获得鱼干' : '使用鱼干' : event.kind === 'tool_granted' ? '获得道具' : '使用道具'}</strong>
            <small>{sourceName(event.source)}{event.toolId ? ` · ${TOOL_NAMES[event.toolId] ?? event.toolId}` : ''}</small></div>
          <b className={event.kind === 'fish' && event.fishDryDelta! < 0 ? 'spend' : 'gain'}>
            {event.kind === 'fish' ? `${event.fishDryDelta! > 0 ? '+' : ''}${number(event.fishDryDelta ?? 0)}` : `×${number(event.quantity)}`}
          </b>
          <time>{new Date(event.occurredAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
        </div>)}
        {!eventsLoading && !events.length && <p className="empty-state">这位玩家当天没有鱼干或道具流水</p>}
      </div>
      {hasMoreEvents && <footer className="economy-activity-pagination"><button className="secondary-button" disabled={eventsLoading} onClick={() => void loadMore()}>{eventsLoading ? '读取中…' : '加载更多'}</button></footer>}
    </section>}
  </div>;
}

function shanghaiToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function number(value: number | undefined): string { return new Intl.NumberFormat('zh-CN').format(value ?? 0); }
function platformName(value: string): string { return ({ all: '全部渠道', wechat: '微信', douyin: '抖音', taptap: 'TapTap', dev: '开发设备' } as Record<string, string>)[value] ?? value; }
function sourceName(value: string): string { return SOURCES[value] ?? value.replaceAll('_', ' '); }
